import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../../app/hooks';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';
import {
    AICA_ASK_AICA_MESSAGE_SENT,
    AICA_GLOBAL_ASK_AICA_MESSAGE_SENT,
} from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import { getCompanyLogo, getFaviconUrl } from '../../../../../../../../utils/urlMetadata';

import AskAicaInputBox from '../AskAicaInputBox/AskAicaInputBox';
import { Button } from '../../../../../../../../common/_custom';
import CopyIcon from '../../../../../../../../assets/Svg/CopyIcon';
import TickIcon from '../../../../../../../../assets/Svg/TickIcon';
import MessagesContainer from '../MessagesContainer/MessagesContainer';
import AskAicaGreeting from '../AskAicaGreeting/AskAicaGreeting';
import useCopyText from '../../../../../../../../common/_custom/Hooks/useCopyText/useCopyText';
import { MessageType, COPY_TARGET, PORTFOLIO_SUGGESTED_QUESTIONS } from '../../../../../../../../store/dashboard/askAica/constants';
import { leftPanelCloseIcon } from '../../../../../../../../assets/hostedassets';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import DeleteIcon from '../../../../../../../../assets/Svg/DeleteIcon';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';
import { AgentInput } from '../../../../../../../../store/dashboard/askAica/actions';
import { ClaimedAttachment } from '../../../../../../../../store/dashboard/askAica/types';
import { CitationSource, OnCitationActivate } from '../MessagesContainer/MessagesContainer.types';
import { useSuggestionsStrip } from '../../hooks/useSuggestionsStrip';
import SuggestionsStrip from '../SuggestionsStrip/SuggestionsStrip';

import { WhatsAppChatView, WhatsAppViewOnlyNotice } from '../WhatsAppChat';

import styles from '../../AskAicaV2.module.scss';

interface LeftPanelProps {
    selectedCompany?: any;
    overviewData?: any;
    onSendMessageRef?: React.MutableRefObject<
        | ((
              message: string,
              attachmentDocIds?: string[],
              flowKey?: string,
              agentInput?: AgentInput,
              pendingUploads?: ClaimedAttachment[],
          ) => void)
        | null
    >;
    onClearChat: () => void;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
    /** URL of the file currently being opened — forwarded to MessagesContainer for the card spinner. */
    openingFileUrl?: string | null;
    /** Resolves + executes what a citation click should do (open doc / navigate section / scroll). */
    onCitationActivate: OnCitationActivate;
    /** Affordance-only tooltip hint for a citation (resolves the live section label). */
    getCitationTitle: (source: CitationSource) => string;
    /** When the reasoning rail is collapsed it renders nothing at all (Figma
        32258:170630), so this header takes over its expand control AND the Close cross —
        otherwise closing Ask AICA would have no visible affordance. */
    rightPanelCollapsed: boolean;
    onExpandRightPanel: () => void;
    onCloseAskAica?: () => void;
    /** Forces the Close control on mobile, where the Right Panel is hidden. */
    isMobile?: boolean;
    /** Opens the conversation drawer on mobile. */
    onOpenSidebar?: () => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
    selectedCompany,
    overviewData,
    onSendMessageRef,
    onClearChat,
    onOpenFile,
    onAddFileToReports,
    openingFileUrl,
    onCitationActivate,
    getCitationTitle,
    rightPanelCollapsed,
    onExpandRightPanel,
    onCloseAskAica,
    isMobile,
    onOpenSidebar,
}) => {
    const user = useAppSelector((state) => state.user.userData);
    const params = useParams();
    const {
        messages,
        isLoading,
        hasPendingClarification,
        isLoadingChatHistory,
        sendMessage,
        registerStreamStartedCallback,
        messagesEndRef,
        fetchChatHistory,
        hasMoreData,
        isClearingChat,
        isViewOnly,
        fileContextOrgId,
        surface,
        selectedConversationId,
        selectedConversationIsDeleted,
        whatsApp,
    } = useAskAicaSurface();
    const [streamStarted, setStreamStarted] = useState(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
    const initialFetchRef = useRef(true);
    // Shared abort holder: every send path (typed, suggestion, badge) stores its
    // abort fn here so the input's stop button cancels any of them.
    const abortStreamRef = useRef<(() => void) | undefined>(undefined);
    const orgId = fileContextOrgId ?? (params.id ? params.id : user?.investorOrgId);

    // Register the streamStarted callback with useAskAica
    useEffect(() => {
        registerStreamStartedCallback(setStreamStarted);
    }, [registerStreamStartedCallback]);

    // Reset scroll flag after scrolling is triggered
    useEffect(() => {
        if (shouldScrollToBottom) {
            // Reset the flag after a short delay to allow the scroll to complete
            const timer = setTimeout(() => {
                setShouldScrollToBottom(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [shouldScrollToBottom]);

    // Get company logo with fallback logic (same as Details component)
    const getCompanyLogoSrc = () => {
        const companyLogo = selectedCompany?.logo;
        const websiteUrl = overviewData?.socialInfo?.websiteUrl;
        const logoFromWebsite = websiteUrl && getCompanyLogo(websiteUrl);
        const faviconFromWebsite = websiteUrl && getFaviconUrl(websiteUrl);
        return companyLogo || logoFromWebsite || faviconFromWebsite;
    };

    const handleSendMessage = useCallback(
        (message: string, attachmentDocIds?: string[], flowKey?: string, agentInput?: AgentInput, pendingUploads?: ClaimedAttachment[]) => {
            // track mixpanel event — distinct event per surface so portfolio and
            // leads analytics stay separable (F-5).
            Mixpanel.track(surface === ASK_AICA_SURFACE.PORTFOLIO ? AICA_GLOBAL_ASK_AICA_MESSAGE_SENT : AICA_ASK_AICA_MESSAGE_SENT, {
                messageLength: message.length,
                userId: user?.userId,
                organizationId: orgId,
            });

            // Reset stream started flag when sending a new message
            setStreamStarted(false);
            // Trigger scroll to bottom immediately when user sends a message
            setShouldScrollToBottom(true);
            // Call the useAskAica hook's sendMessage function
            const abort = sendMessage(message, flowKey, agentInput, attachmentDocIds, pendingUploads);
            abortStreamRef.current = abort;
            return abort;
        },
        [user?.userId, orgId, sendMessage, surface],
    );

    // Mobile Portfolio chat: the "✦ Suggestions" toggle + strip, same behaviour
    // as the company report's mobile Ask AICA (useMobileAskAicaLogic) — open by
    // default while the chat is empty, the toggle overrides it. Desktop shows no
    // suggestions here.
    const showSuggestionsToggle = !!isMobile && surface === ASK_AICA_SURFACE.PORTFOLIO && !isViewOnly;
    const isChatEmpty = messages.length === 0 && !isLoadingChatHistory;
    const trackingProps = useMemo(
        () => ({ pnoId: user?.investorOrgId, userId: user?.userId, organizationId: orgId }),
        [user?.investorOrgId, user?.userId, orgId],
    );
    const { suggestionsVisible, toggleSuggestions, handleSuggestionTap, handleComposerSend, resetSuggestions } = useSuggestionsStrip({
        enabled: showSuggestionsToggle,
        isChatEmpty,
        isLoading: isLoading || !!hasPendingClarification,
        resetKey: selectedConversationId,
        trackingProps,
        onSend: handleSendMessage,
    });

    // Expose the composer-send function to parent component — routed through
    // handleComposerSend so every send path closes the strip the same way.
    useEffect(() => {
        if (onSendMessageRef) {
            onSendMessageRef.current = handleComposerSend;
        }

        // Cleanup ref on unmount or when handleComposerSend changes
        return () => {
            if (onSendMessageRef) {
                onSendMessageRef.current = null;
            }
        };
    }, [onSendMessageRef, handleComposerSend]);

    const handleClearChat = useCallback(() => {
        resetSuggestions();
        onClearChat();
    }, [resetSuggestions, onClearChat]);

    const retryMessage = (index) => {
        const messageToRetry = messages[index + 1];
        if (messageToRetry) {
            handleComposerSend(messageToRetry.content, undefined, messageToRetry.flowKey, messageToRetry.agentInput);
        }
    };

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const { copiedId, copyHtmlContent } = useCopyText();

    /**
     * Copies the full chat content in chronological order (oldest first).
     * Assembles HTML and plain text from rendered DOM elements using data attributes.
     */
    const handleCopyFullChat = useCallback(() => {
        if (!messagesContainerRef.current) return;
        const messageEls = Array.from(messagesContainerRef.current.querySelectorAll('[data-message-type]')).reverse() as HTMLElement[];
        let html = '';
        let plain = '';
        messageEls.forEach((el) => {
            const type = el.getAttribute('data-message-type');
            if (type === MessageType.USER) {
                const bubble = el.querySelector('p') as HTMLElement | null;
                if (!bubble) return;
                html += `<p><b>You:</b> ${bubble.innerHTML}</p>`;
                plain += `You: ${bubble.innerText}\n\n`;
                return;
            }
            const content = el.querySelector('[data-content-role="assistant"]') as HTMLElement | null;
            if (!content) return;
            html += `<p><b>AICA:</b></p>${content.innerHTML}<br/>`;
            plain += `AICA:\n${content.innerText}\n\n`;
        });
        copyHtmlContent(html, plain.trim(), COPY_TARGET.FULL_CHAT);
    }, [copyHtmlContent]);

    const logoSrc = getCompanyLogoSrc();

    return (
        <div className={styles.LeftPanel}>
            {/* Header with Company/User Info */}
            <div className={styles.Header}>
                {isMobile ? (
                    <button
                        type="button"
                        className={styles.SidebarToggleBtn}
                        onClick={onOpenSidebar}
                        aria-label="Open conversations"
                        title="Open conversations"
                    >
                        <img src={leftPanelCloseIcon} alt="" width="20" height="20" />
                    </button>
                ) : (
                    <div className={styles.CompanyInfoContainer}>
                        <div className={styles.CompanyLogo}>
                            {logoSrc ? (
                                <img
                                    src={logoSrc}
                                    alt="Company Logo"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const parent = e.currentTarget.parentElement;
                                        if (parent && !parent.querySelector(`.${styles.PlaceholderLogo}`)) {
                                            const placeholder = document.createElement('div');
                                            placeholder.className = styles.PlaceholderLogo;
                                            placeholder.textContent =
                                                selectedCompany?.orgName?.charAt(0) || user?.userName?.charAt(0) || 'U';
                                            parent.appendChild(placeholder);
                                        }
                                    }}
                                />
                            ) : (
                                <div className={styles.PlaceholderLogo}>
                                    {selectedCompany?.orgName?.charAt(0) || user?.userName?.charAt(0) || 'U'}
                                </div>
                            )}
                        </div>
                        <h2 className={styles.CompanyName}>{selectedCompany?.orgName || user?.userName || 'User'}</h2>
                        {/* Cross-chat citation click landed on a conversation that's since
                            been soft-deleted (Athena's share-view still serves its history —
                            it just flags is_deleted). Own/team-conversation switching never
                            surfaces this today since a deleted row drops out of the sidebar
                            list first (setConversations' own reset) — this badge only ever
                            fires for a navigation target outside that list. */}
                        {!!selectedConversationId && selectedConversationIsDeleted && <span className={styles.DeletedBadge}>Deleted</span>}
                    </div>
                )}
                <div className={styles.HeaderActions}>
                    {!isMobile && (
                        <button
                            className={styles.CopyFullChatButton}
                            onClick={handleCopyFullChat}
                            title="Copy full chat"
                            disabled={!messages.length || whatsApp?.isOpen}
                        >
                            {copiedId === COPY_TARGET.FULL_CHAT ? (
                                <TickIcon width={14} height={14} color="#49A71B" />
                            ) : (
                                <CopyIcon width="14" height="14" colour="#6b7280" />
                            )}
                        </button>
                    )}
                    {!isMobile && rightPanelCollapsed && (
                        <div className={styles.HeaderRailActions}>
                            <button
                                type="button"
                                className={styles.HeaderExpandRailBtn}
                                onClick={onExpandRightPanel}
                                title="Expand reasoning panel"
                                aria-label="Expand reasoning panel"
                            >
                                <img src={leftPanelCloseIcon} alt="" width="20" height="20" />
                            </button>
                            <button
                                type="button"
                                className={styles.HeaderCloseBtn}
                                onClick={onCloseAskAica}
                                aria-label="Close Ask AICA"
                                data-testid="ask-aica-close-button"
                            >
                                <CrossIcon colour="#000000" width="12" height="12" />
                            </button>
                        </div>
                    )}
                    {isMobile && (
                        <div className={styles.HeaderRailActions}>
                            {!isViewOnly && !!selectedConversationId && (
                                <button
                                    type="button"
                                    className={styles.HeaderDeleteIconBtn}
                                    onClick={handleClearChat}
                                    aria-label="Delete chat"
                                    title="Delete chat"
                                    disabled={isClearingChat}
                                >
                                    <DeleteIcon className="" width="16" height="16" />
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.HeaderCloseBtn}
                                onClick={onCloseAskAica}
                                aria-label="Close Ask AICA"
                                data-testid="ask-aica-close-button"
                            >
                                <CrossIcon colour="#000000" width="12" height="12" />
                            </button>
                        </div>
                    )}
                    {!isMobile && !isViewOnly && !!selectedConversationId && (
                        <Button
                            variant="white"
                            size="tiny"
                            text="Delete Chat"
                            style={{ margin: '0' }}
                            textstyle={{ fontSize: '0.75rem' }}
                            onClick={handleClearChat}
                            // D4: rendered only when the FE holds an explicit conversation
                            // target — the header action must never lean on the server's
                            // active-conversation fallback.
                            disabled={isClearingChat}
                        />
                    )}
                </div>
            </div>

            {/* Loading More Messages Overlay */}
            {isLoadingChatHistory && !initialFetchRef.current && (
                <div className={styles.LoadingOverlay}>
                    <div className={styles.LoadingContent}>
                        <div className={styles.LoadingText}>Loading more messages</div>
                        <div className={styles.TypingIndicator}>
                            <div className={styles.TypingDot}></div>
                            <div className={styles.TypingDot}></div>
                            <div className={styles.TypingDot}></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Area */}
            <div className={styles.ChatArea}>
                {whatsApp?.isOpen ? (
                    <WhatsAppChatView />
                ) : isMobile && messages.length === 0 && !isLoadingChatHistory ? (
                    // Mobile renders the greeting here; the input box's centred one is sized for desktop.
                    <div className={styles.MobileEmptyBody}>
                        <AskAicaGreeting />
                    </div>
                ) : (
                    <MessagesContainer
                        streamStarted={streamStarted}
                        isLoadingChatHistory={isLoadingChatHistory}
                        messagesEndRef={messagesEndRef}
                        fetchChatHistory={fetchChatHistory}
                        hasMoreData={hasMoreData}
                        retryMessage={isViewOnly ? undefined : retryMessage}
                        scrollToBottom={shouldScrollToBottom}
                        initialFetchRef={initialFetchRef}
                        containerRef={messagesContainerRef}
                        onOpenFile={onOpenFile}
                        onAddFileToReports={onAddFileToReports}
                        openingFileUrl={openingFileUrl}
                        onCitationActivate={onCitationActivate}
                        getCitationTitle={getCitationTitle}
                    />
                )}
                {suggestionsVisible && (
                    <SuggestionsStrip
                        questions={PORTFOLIO_SUGGESTED_QUESTIONS}
                        onSelect={handleSuggestionTap}
                        testId="portfolio-suggestions-strip"
                    />
                )}
                {whatsApp?.isOpen && <WhatsAppViewOnlyNotice />}
                {!isViewOnly && (
                    <AskAicaInputBox
                        onSendMessage={handleComposerSend}
                        isLoading={isLoading}
                        abortRef={abortStreamRef}
                        mobileLayout={isMobile}
                        suggestionsToggle={showSuggestionsToggle ? { open: suggestionsVisible, onToggle: toggleSuggestions } : undefined}
                    />
                )}
            </div>
        </div>
    );
};

export default LeftPanel;
